import React, { useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Alert,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LoginIcon from "@mui/icons-material/Login";
import HomeIcon from "@mui/icons-material/Home";
import { useAuth } from "../../../context/AuthContext";
import { login as loginApi } from "../../../Services/apiService";
import logo from "../../../Assets/logo.png";

const Login = () => {
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await loginApi(formData.email, formData.password);
      const { data } = response.data;

      if (data.active === 0) {
        navigate("/reactivate-account", {
          state: { email: data.email, userid: data.id, verification_code: data.verification_code },
        });
        return;
      }

      login(data, data.token);

      let destination = "/";
      if (data.type === "1" || data.type === "IgRANDaDMIN1344@$") {
        destination = "/admin-dashboard";
      } else if (data.type === "broker") {
        destination = "/broker-dashboard";
      } else if (data.type === "0" && data.status === 1) {
        destination = "/user-dashboard";
      } else if (data.type === "0" && data.status === 0) {
        destination = "/user-dashboard-two";
      }
      navigate(destination);

    } catch (err) {
      if (err.response?.status === 401 && err.response?.data?.data?.active === 0) {
        navigate("/reactivate-account", {
          state: { email: err.response?.data?.data?.email, userid: err.response?.data?.data?.id },
        });
      } else {
        setError(err.response?.data?.message || "حدث خطأ أثناء الاتصال بالسيرفر.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)",
        position: "relative",
        py: 6,
        px: 2,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            "radial-gradient(circle, rgba(243, 111, 33, 0.03) 1px, transparent 1px)",
          backgroundSize: "30px 30px",
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="xs" sx={{ position: "relative", zIndex: 1 }}>
        {/* زر العودة للصفحة الرئيسية */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
          <Button
            component={RouterLink}
            to="/"
            variant="outlined"
            startIcon={<HomeIcon />}
            sx={{
              borderRadius: 3,
              borderWidth: 2,
              borderColor: "#f36f21",
              color: "#f36f21",
              fontWeight: 600,
              px: 3,
              py: 1,
              textTransform: "none",
              "&:hover": {
                borderWidth: 2,
                borderColor: "#e65100",
                background: "rgba(243, 111, 33, 0.05)",
                transform: "translateY(-2px)",
              },
            }}
          >
            العودة للصفحة الرئيسية
          </Button>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 5 },
            borderRadius: 5,
            border: "2px solid #e8e8e8",
            background: "white",
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
            transition: "all 0.3s ease",
            "&:hover": {
              boxShadow: "0 12px 48px rgba(243, 111, 33, 0.12)",
              borderColor: "#f36f21",
            },
          }}
        >
          {/* Logo & App Name */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{
                height: { xs: 70, md: 90 },
                width: "auto",
                mb: 2,
                filter: "drop-shadow(0 4px 8px rgba(243, 111, 33, 0.2))",
              }}
            />
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                textAlign: "center",
                fontSize: { xs: "1.5rem", md: "2rem" },
              }}
            >
              ريستو كاش
            </Typography>
          </Box>

          {/* Icon Header */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                background: "linear-gradient(135deg, rgba(243, 111, 33, 0.1) 0%, rgba(230, 81, 0, 0.1) 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LoginIcon sx={{ fontSize: 30, color: "#f36f21" }} />
            </Box>
          </Box>

          <Typography
            variant="h5"
            align="center"
            gutterBottom
            fontWeight={800}
            sx={{
              color: "text.primary",
              mb: 1,
            }}
          >
            تسجيل الدخول
          </Typography>
          <Typography
            variant="body2"
            align="center"
            sx={{ mb: 3, color: "text.secondary", lineHeight: 1.8 }}
          >
            مرحباً بعودتك! سجّل دخولك للمتابعة
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "error.light",
              }}
            >
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="البريد الإلكتروني"
                  name="email"
                  type="email"
                  fullWidth
                  value={formData.email}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: "#f36f21" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      "&:hover fieldset": { borderColor: "#f36f21" },
                      "&.Mui-focused fieldset": {
                        borderColor: "#f36f21",
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="كلمة المرور"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  value={formData.password}
                  onChange={handleChange}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: "#f36f21" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      "&:hover fieldset": { borderColor: "#f36f21" },
                      "&.Mui-focused fieldset": {
                        borderColor: "#f36f21",
                        borderWidth: 2,
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                  sx={{
                    py: 2,
                    fontSize: "1.1rem",
                    fontWeight: 700,
                    borderRadius: 3,
                    textTransform: "none",
                    background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
                    boxShadow: "0 6px 20px rgba(243, 111, 33, 0.3)",
                    "&:hover": {
                      background: "linear-gradient(135deg, #e65100 0%, #d84315 100%)",
                      boxShadow: "0 8px 28px rgba(243, 111, 33, 0.4)",
                      transform: "translateY(-2px)",
                    },
                    "&:disabled": {
                      background: "rgba(243, 111, 33, 0.5)",
                    },
                  }}
                >
                  {loading ? "جاري تسجيل الدخول..." : "تسجيل الدخول"}
                </Button>
              </Grid>
            </Grid>
          </form>

          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              ليس لديك حساب؟{" "}
              <Box
                component={RouterLink}
                to="/signup"
                sx={{
                  color: "#f36f21",
                  fontWeight: 700,
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                إنشاء حساب جديد
              </Box>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
